// Pure, side-effect-free dot-styling logic for the map layer.
// Extracted so it can be unit-tested without a Leaflet or DOM dependency.
//
// The styling rules below come from the Map Behavior Specification sections
// 7.2 (Dot rendering) and 10 (Central logistics). Radius constants were
// halved on 2026-04-21 in response to product feedback that the previous
// dot sizes were too large, and the hollow central-logistics ring was
// changed to a neutral stroke so the "open circle" treatment no longer
// encodes phone color. Any change here must be reflected in the spec.

import type { DayDot } from './types';

export interface DotStyle {
  radius: number;
  /** Stroke color (applied only when weight > 0). */
  color: string;
  /** Stroke width in pixels. 0 means "no stroke" for a pure fill. */
  weight: number;
  /** Stroke opacity. Mirrors API opacity so dots dim uniformly. */
  opacity: number;
  /** Fill color. */
  fillColor: string;
  /** Fill opacity. Taken directly from the API, or reduced for hollow dots. */
  fillOpacity: number;
}

// --- Size constants --------------------------------------------------------
// Size history:
//   - Original:           default 3,     logistics 5,     central 8,   secondary 2.5
//   - 2026-04-21 halved:  default 1.5,   logistics 2.5,   central 4,   secondary 1.25
//   - 2026-04-21 +25%:    default 1.875, logistics 3.125, central 4,   secondary 1.5625
//   - NOW     filled+50%: default 2.8125, logistics 4.6875, central 4, secondary 2.34375
// The +50% bump applies ONLY to filled markers. The hollow central-logistics
// ring stays at radius 4 so the open-circle treatment remains restrained and
// does not encode or compete with state color. Filled "secondary" radius is
// also bumped 50% so that any phone-detail view rendering stays visually
// consistent, even though those secondary markers are explicitly NOT drawn
// on the main playback map (see MapView.tsx).
export const DOT_RADIUS_DEFAULT = 2.8125;   // 1.875 * 1.5
export const DOT_RADIUS_LOGISTICS = 4.6875; // 3.125 * 1.5
export const DOT_RADIUS_CENTRAL = 4;        // hollow ring, unchanged
/**
 * Radius for secondary filled dots at each `central_locations` entry.
 * NOT rendered on the main playback map (see MapView.tsx): secondary
 * markers were filled with the phone color, which broke the "one color
 * per state per day" invariant for central-logistics phones whose same-
 * day locations span states. Kept exported for the phone-detail view
 * and tests, where point-per-location rendering is scoped to a single
 * phone on user click and does not mix across states on the same day.
 */
export const DOT_RADIUS_CENTRAL_SECONDARY = 2.34375; // 1.5625 * 1.5

/**
 * Neutral stroke color for the hollow central-logistics ring. The ring no
 * longer encodes phone color (the phone's color already appears via the
 * small secondary dots drawn at each same-day location). A soft warm-white
 * reads against the CARTO dark basemap without competing with the colored
 * dots nearby.
 */
export const CENTRAL_RING_STROKE = '#d8d8d8';

/**
 * Derive the CircleMarker style for a single day dot.
 *
 * Rules:
 *   - Fill color: use `color` field from API as marker color for normal
 *     and logistics dots. Central hollow rings do NOT fill with phone
 *     color; their fill is transparent.
 *   - Opacity: use `opacity` field from API directly. Do not recompute
 *     from days_stale.
 *   - Size: default dot radius 2.8125 px; logistics 4.6875 px; central hollow ring 4 px (unchanged).
 *   - Stroke: if star >= 1, draw a thin contrasting outline.
 *   - Central logistics (Section 10): draw a hollow ring at 4 px radius
 *     with a neutral stroke (CENTRAL_RING_STROKE). Starred central
 *     phones keep the white outline. No phone color is applied to the
 *     ring itself \u2014 color is carried by the secondary dots drawn by
 *     MapView at each same-day location.
 *
 * Implementation notes:
 *   - The live API encodes `star` as an integer rank (0 or 3..5), so
 *     "has a star" is treated as `star >= 1` to match the spec intent.
 *   - Non-starred, non-central dots receive `weight: 0` so the fill
 *     color reads cleanly against the dark basemap rather than being
 *     ringed by itself.
 */
export function dotStyle(dot: DayDot): DotStyle {
  const starred = dot.star >= 1;
  const isCentral = dot.central_logistics === 1;

  if (isCentral) {
    // Section 10: hollow primary marker with a neutral stroke. Fill is
    // transparent so the ring reads as a pure outline against the
    // basemap. Starred central phones keep the white outline.
    return {
      radius: DOT_RADIUS_CENTRAL,
      color: starred ? '#ffffff' : CENTRAL_RING_STROKE,
      weight: starred ? 1.25 : 1,
      opacity: dot.opacity,
      fillColor: CENTRAL_RING_STROKE,
      fillOpacity: 0,
    };
  }

  const radius = dot.logistics_flag === 1 ? DOT_RADIUS_LOGISTICS : DOT_RADIUS_DEFAULT;
  return {
    radius,
    color: starred ? '#ffffff' : dot.color,
    weight: starred ? 1 : 0,
    opacity: dot.opacity,
    fillColor: dot.color,
    fillOpacity: dot.opacity,
  };
}
